import { useState } from "react";

import { PatientMedication, PatientMedicationStatus } from "~/common/types";
import { formatMedicationName } from "~/common/utils";
import {
  generateDatesRange,
  generateDiscontinuationTitle,
  generateMedicationCardTitle,
} from "~/providers/utils";
import { generateDefaultPatientMedication } from "~/providers/utils/medications/generateDefaultPatientMedication";
import { DiscontinueMedicationModal } from "../DiscontinueMedicationModal";
import { UpsertMedicationModal } from "../UpsertMedicationModal";
import { MedicationCardDetails } from "./MedicationCardDetails";
import { MedicationCardLayout } from "./MedicationCardLayout";

export interface MedicationCardProps extends PatientMedication {
  status: PatientMedicationStatus;
}

export const MedicationCard = ({
  id: patientMedicationId,
  organization_patient,
  medication,
  status,
  discontinued_internal_note,
  discontinued_message_to_patient,
  discontinued_at,
  start_date,
  patient_medications_details,
  scripts_statuses,
  merged_medication_records,
}: MedicationCardProps) => {
  const { patient } = organization_patient;

  const [modifyMedication, setModifyMedication] = useState(false);
  const [showDiscontinueModal, setShowDiscontinueModal] = useState(false);
  const toggleDiscontinue = () => setShowDiscontinueModal((prev) => !prev);
  const toggleModifyMedication = () => setModifyMedication((prev) => !prev);

  const medicationTitle = generateMedicationCardTitle(
    medication,
    patient_medications_details[0],
  );
  const lastScriptStatusChange = scripts_statuses?.[0];

  return (
    <>
      <MedicationCardLayout
        status={status}
        areButtonsDisabled={!!discontinued_at}
        title={medicationTitle}
        subtitle={generateDatesRange(start_date, discontinued_at)}
        handleDiscontinue={toggleDiscontinue}
        handleModify={toggleModifyMedication}
        isPaused={lastScriptStatusChange?.is_paused}
      >
        <MedicationCardDetails
          status={status}
          discontinuationTitle={generateDiscontinuationTitle(discontinued_at)}
          internalNote={discontinued_internal_note}
          patientNote={discontinued_message_to_patient}
          medicationName={formatMedicationName(medication)}
          mergedMedicationRecords={merged_medication_records}
        />
      </MedicationCardLayout>

      <DiscontinueMedicationModal
        show={showDiscontinueModal}
        onClose={toggleDiscontinue}
        patientId={patient.id}
        patientMedicationId={patientMedicationId}
      />

      <UpsertMedicationModal
        key={patient_medications_details[0].id}
        patientId={patient.id}
        show={modifyMedication}
        handleClose={toggleModifyMedication}
        defaultMedication={medication}
        defaultPatientMedicationId={patientMedicationId}
        defaultPatientMedicationDetails={generateDefaultPatientMedication(
          patient_medications_details[0],
        )}
      />
    </>
  );
};
